import { createContext, PropsWithChildren, useContext, useEffect } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { getDataFoundationEvent } from "@/utils/data-foundations/data-foundation-types";

import { ProductKey } from "../models";

import { useCloudStatus, useSearchParameters } from ".";

export function useCloudStatusBundleRedirection() {
    return useContext(CloudStatusBundleRedirectionContext);
}

const CloudStatusBundleRedirectionContext = createContext({});

export const CloudStatusBundleRedirectionProvider =
    /* istanbul ignore next */
    ({ children }: PropsWithChildren) => {
        const {
            createCloudStatus,
            triggerOnSuccess,
            onSuccessDuration,
            readyPollCloudStatus,
            pollCloudStatus,
            setFireOnRedirectAnalytics,
            onSuccess,
            createRedirectUrl
        } = useCloudStatus();
        const { createAnalyticsEvent } = useAnalyticsEvents();
        const { products, edition } = useSearchParameters();
        const productKey = (
            products.length > 0 ? products[0] : undefined
        ) as ProductKey;

        useEffect(() => {
            if (triggerOnSuccess) {
                onSuccess(Date.now() - onSuccessDuration);
            }
        }, [triggerOnSuccess]);

        useEffect(() => {
            if (readyPollCloudStatus) {
                pollCloudStatus();
            }
        }, [readyPollCloudStatus, pollCloudStatus]);

        // On provisioning finish UFO, analytics and redirection handling
        useEffect(() => {
            if (createCloudStatus !== "success") {
                return;
            }
            const redirectUrl = createRedirectUrl(true);

            setFireOnRedirectAnalytics(true);

            createAnalyticsEvent({
                ...getDataFoundationEvent("ProductNavigationInitialized"),
                actionSubjectId: "newSite",
                attributes: {
                    productUrl: redirectUrl,
                    productKey,
                    edition
                }
            }).fire();

            window.location.assign(redirectUrl);
        }, [
            createCloudStatus,
            edition,
            productKey,
            setFireOnRedirectAnalytics,
            createAnalyticsEvent,
            createRedirectUrl
        ]);

        return (
            <CloudStatusBundleRedirectionContext.Provider value={{}}>
                {children}
            </CloudStatusBundleRedirectionContext.Provider>
        );
    };
